import { Tag, Text } from '@zillow/constellation-10';
import { MapPin, Star } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import { LucideIcon } from '@/src/components/LucideIcon';
import { MapInfoCardShell } from '@/src/components/features/map/InfoCard/MapInfoCardShell';
import { getPOICategoryLabel, getPOISearchConfig, type POIPlace } from './types';
import { POI_ICON_GROUP_ICONS } from './poiCategoryIcons';
import {
  poiInfoCardAttributionList,
  poiInfoCardCloseButton,
  poiInfoCardContainer,
  poiInfoCardContent,
  poiInfoCardHeader,
  poiInfoCardMeta,
  poiInfoCardMetaItem,
  poiInfoCardTitle,
} from './poiInfoCard.styles';

type PoiInfoCardProps = {
  place: POIPlace;
  onClose: () => void;
};

function formatRating(rating: number): string {
  return rating.toFixed(1).replace(/\.0$/, '');
}

export function PoiInfoCard({ place, onClose }: PoiInfoCardProps) {
  const t = useTranslations('hdp');
  const tCommon = useTranslations('common');
  const format = useFormatter();
  const categoryLabel = getPOICategoryLabel(place.category, (labelKey) => t(labelKey));
  const categoryConfig = getPOISearchConfig(place.category);
  const ratingText = place.rating === undefined ? null : formatRating(place.rating);
  const ratingLabel =
    ratingText && place.userRatingCount !== undefined
      ? t('poiRatingWithCount', { rating: ratingText, count: place.userRatingCount })
      : ratingText
        ? t('poiRating', { rating: ratingText })
        : null;

  return (
    <MapInfoCardShell
      testId="poi-info-card"
      closeTestId="poi-info-card-close"
      closeLabel={tCommon('close')}
      containerClassName={poiInfoCardContainer}
      closeButtonClassName={poiInfoCardCloseButton}
      onClose={onClose}
    >
      <article className={poiInfoCardContent} aria-label={t('poiInfoWindowAriaLabel', { name: place.name })}>
        <div className={poiInfoCardHeader}>
          <Tag
            tone="teal"
            size="sm"
            icon={<LucideIcon icon={POI_ICON_GROUP_ICONS[categoryConfig.iconGroup]} size="sm" />}
          >
            {categoryLabel}
          </Tag>
          <h3 className={poiInfoCardTitle}>{place.name}</h3>
        </div>

        {(ratingLabel || place.address) && (
          <div className={poiInfoCardMeta}>
            {ratingLabel && (
              <span className={poiInfoCardMetaItem} aria-label={ratingLabel}>
                <LucideIcon icon={Star} size="sm" />
                <span>{ratingText}</span>
                {place.userRatingCount !== undefined && <span>({format.number(place.userRatingCount)})</span>}
              </span>
            )}
            {place.address && (
              <span className={poiInfoCardMetaItem}>
                <LucideIcon icon={MapPin} size="sm" />
                <span>{place.address}</span>
              </span>
            )}
          </div>
        )}

        {place.attributionHtml && place.attributionHtml.length > 0 && (
          <Text asChild color="text.subtle">
            <div className={poiInfoCardAttributionList} aria-label={t('poiAttribution')}>
              {place.attributionHtml.map((html) => (
                <span key={html} dangerouslySetInnerHTML={{ __html: html }} />
              ))}
            </div>
          </Text>
        )}
      </article>
    </MapInfoCardShell>
  );
}
